import {
  FormInput,
  FormSelect,
  Form,
  FormField,
  V2Loader,
  FormItem,
  FormLabel,
  FormMessage,
  FormCheckbox,
  FormCurrencyInput,
} from "web-design-system";
import {
  RedemptionBy,
  REMDEMPTION_BY_OPTIONS,
  TransactionOrderType,
} from "creso-core/constants";
import {
  useScheme,
  useCreateSwitchTransaction,
  useUpdateSwitchTransaction,
  useHoldingsData,
  useGetHoldingFolios,
} from "creso-core/hooks";
import { useCallback, useContext, useMemo } from "react";
import { toast } from "sonner";
import {
  FolioWiseHolding,
  SchemeWiseHolding as HoldingScheme,
} from "creso-core/types";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { switchInFormValidationSchema } from "creso-core/zod";
import { handleFormValidationErrors } from "@/utils/error";
import { InfoIcon } from "lucide-react";
import {
  calculateCapitalGains,
  convertToCurrency,
  isSwitchOrder,
} from "creso-core/utils";
import {
  HoldingDetails,
  HoldingFoliosDropdown,
  HoldingSchemesDropdown,
} from "web-core";
import { TransactionContext } from "./transaction-wizard";
import { SchemesSearchDropdown } from "./schemes-search-dropdown";
import FormActions from "./form-actions";

type FormData = z.infer<typeof switchInFormValidationSchema>;

function getDefaultFormData(): FormData {
  return {
    switch_out_scheme: null,
    folio: null,
    switch_in_scheme: null,
    switchBy: RedemptionBy.AMOUNT,
    amount: null,
    units: null,
    switchAllUnits: false,
  };
}

export default function SwitchForm() {
  const {
    selectedOrder,
    investment_account_guid = "",
    updateContext,
  } = useContext(TransactionContext);

  const switch_in_scheme_id =
    selectedOrder?.type === TransactionOrderType.SWITCH
      ? selectedOrder.switch_in_scheme_id
      : 0;

  const { data: switchInScheme } = useScheme<"all">(switch_in_scheme_id, "all");

  const { data: holdingsSchemes, isLoading: isHoldingsSchemesLoading } =
    useHoldingsData(investment_account_guid);

  const switch_out_scheme_isin =
    selectedOrder?.type === TransactionOrderType.SWITCH
      ? selectedOrder.switch_out_scheme_isin
      : "";

  const { data: holdingFolios } = useGetHoldingFolios(
    investment_account_guid,
    switch_out_scheme_isin,
    !!switch_out_scheme_isin && !!investment_account_guid
  );

  const createSwitchTransaction = useCreateSwitchTransaction(
    investment_account_guid
  );

  const updateSwitchTransaction = useUpdateSwitchTransaction(
    investment_account_guid
  );

  const folio = isSwitchOrder(selectedOrder)
    ? holdingFolios?.find((d) => d.folio_id === selectedOrder.folio_id)
    : null;

  const defaultAmount = useMemo(() => {
    if (selectedOrder?.amount) return selectedOrder.amount;
    if (selectedOrder?.units) return "";
    const folioAmount = Number(folio?.amount);
    return Number.isNaN(folioAmount) ? "" : folioAmount.toFixed(2);
  }, [folio, selectedOrder]);

  const defaultUnits = useMemo(() => {
    if (selectedOrder?.units) return selectedOrder.units;
    if (selectedOrder?.amount) return "";
    const folioUnits = Number(folio?.units);
    return Number.isNaN(folioUnits) ? "" : folioUnits.toFixed(2);
  }, [folio, selectedOrder]);

  const form = useForm({
    values: isSwitchOrder(selectedOrder)
      ? {
          switch_out_scheme:
            holdingsSchemes?.find(
              (d) => d.scheme_isin === selectedOrder.switch_out_scheme_isin
            ) ?? null,
          folio: folio ?? null,
          switch_in_scheme: switchInScheme ?? null,
          switchBy: selectedOrder.units
            ? RedemptionBy.UNITS
            : RedemptionBy.AMOUNT,
          amount: defaultAmount,
          units: defaultUnits,
          switchAllUnits:
            selectedOrder.amount === null && selectedOrder.units === null,
        }
      : getDefaultFormData(),
    resolver: zodResolver(switchInFormValidationSchema),
  });

  const capitalGainValues = useWatch({
    control: form.control,
    name: ["switchBy", "folio", "amount", "units", "switchAllUnits"],
  });

  const capitalGains = useMemo(() => {
    const [switchBy, folio, amount, units, switchAllUnits] = capitalGainValues;
    if (!folio) return null;
    return calculateCapitalGains(
      folio as FolioWiseHolding,
      switchAllUnits ? RedemptionBy.UNITS : switchBy,
      switchAllUnits
        ? (folio as FolioWiseHolding).units
        : ((switchBy === RedemptionBy.AMOUNT ? amount : units) as string)
    );
  }, [capitalGainValues]);

  const infoText = capitalGains
    ? `Approx. LTCG: ${convertToCurrency(capitalGains.ltcg)}, STCG: ${convertToCurrency(capitalGains.stcg)}`
    : null;

  const onSubmit = useCallback(
    async (values: FormData) => {
      try {
        if (
          values.switch_out_scheme &&
          values.switch_in_scheme &&
          values.folio
        ) {
          const payload = {
            investment_account_guid,
            switch_out_scheme_isin: values.switch_out_scheme.scheme_isin,
            folio_id: values.folio.folio_id,
            switch_in_scheme_isin: values.switch_in_scheme?.scheme_isin,
            ...(values.switchAllUnits
              ? {}
              : values.switchBy === RedemptionBy.UNITS
                ? {
                    units: values.units,
                  }
                : {
                    amount: values.amount,
                  }),
          };

          if (selectedOrder?.id) {
            await updateSwitchTransaction.mutateAsync({
              id: selectedOrder.id,
              ...payload,
            });
          } else {
            await createSwitchTransaction.mutateAsync(payload);
          }
          form.reset(getDefaultFormData());
          updateContext?.({
            selectedOrder: null,
          });
        }
      } catch (error) {
        handleFormValidationErrors(form, error);
        toast.error(`Failed to save order details`, {
          description: (error as Error).message,
        });
      }
    },
    [
      createSwitchTransaction,
      investment_account_guid,
      selectedOrder,
      updateSwitchTransaction,
      updateContext,
      form,
    ]
  );

  return (
    <div className="flex flex-col flex-1">
      <h5 className="mt-1">
        {selectedOrder?.id ? "Update" : "Create"} Switch order
      </h5>
      <div className="h-px bg-border w-full mt-3 mb-4"></div>
      <V2Loader isLoading={isHoldingsSchemesLoading}>
        <Form {...form}>
          <form
            className="flex flex-col flex-1 px-1 space-y-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="grid grid-cols-5 w-full gap-x-2 gap-y-4">
              <FormField
                className="col-span-2"
                name="switch_out_scheme"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From scheme</FormLabel>
                    <HoldingSchemesDropdown
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        form.setValue("folio", null);
                        const switch_in_scheme =
                          form.getValues("switch_in_scheme");
                        if (switch_in_scheme?.amc_id !== value.amc_id) {
                          form.setValue("switch_in_scheme", null);
                        }
                      }}
                      investmentAccountGuid={investment_account_guid}
                      schemefilter={(scheme: HoldingScheme) =>
                        scheme.switch_allowed
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                className="col-span-1"
                name="folio"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Folio</FormLabel>
                    <HoldingFoliosDropdown
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        form.setValue("switchAllUnits", false);
                      }}
                      investmentAccountGuid={investment_account_guid}
                      schemeIsin={form.watch("switch_out_scheme")?.scheme_isin}
                      filter={(folio) => folio.switch_allowed}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                className="col-span-2"
                name="switch_in_scheme"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>To scheme</FormLabel>
                    <SchemesSearchDropdown
                      placeholder="Select scheme"
                      value={
                        field.value
                          ? {
                              label: field.value.scheme_name,
                              value: field.value.scheme_isin,
                            }
                          : null
                      }
                      onChange={field.onChange}
                      type="all"
                      amc_id={
                        form.watch("switch_out_scheme")?.amc_id ?? undefined
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("folio") && (
                <HoldingDetails
                  className="col-span-5"
                  amount={form.watch("folio")?.amount || ""}
                  units={form.watch("folio")?.units || ""}
                  nav={form.watch("folio")?.nav || ""}
                />
              )}
            </div>

            <div className="grid grid-cols-2 w-full gap-2">
              <FormField
                className="col-span-1"
                control={form.control}
                name="switchBy"
                render={({ field }) => (
                  <FormSelect
                    label="Switch by"
                    options={REMDEMPTION_BY_OPTIONS}
                    {...field}
                    onChange={(value) => {
                      field.onChange(value);
                      form.setValue("amount", "");
                      form.setValue("units", "");
                      form.setValue("switchAllUnits", false);
                    }}
                  />
                )}
              />
              {form.watch("switchBy") === RedemptionBy.AMOUNT ? (
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormCurrencyInput
                      label="Amount"
                      {...field}
                      value={field.value || ""}
                      disabled={!!form.watch("switchAllUnits")}
                    />
                  )}
                />
              ) : (
                <FormField
                  className="col-span-1"
                  control={form.control}
                  name="units"
                  render={({ field }) => (
                    <FormInput
                      label="Units"
                      {...field}
                      value={field.value || ""}
                      type="number"
                      disabled={!!form.watch("switchAllUnits")}
                    />
                  )}
                />
              )}
            </div>
            <FormField
              name={`switchAllUnits`}
              render={({ field }) => (
                <FormCheckbox
                  className="col-span-2"
                  checked={field.value}
                  label="Switch all units"
                  {...field}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                    if (!value) return;
                    form.setValue(`units`, form.watch(`folio.units`));
                    form.setValue(`amount`, form.watch(`folio.amount`));
                  }}
                />
              )}
            />
            {infoText && (
              <div className="bg-card border border-border col-span-2 flex items-center gap-2 rounded-md p-2">
                <InfoIcon className="size-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {infoText}
                </span>
              </div>
            )}

            <FormActions
              dirty={form.formState.isDirty}
              onReset={() => form.reset()}
              isUpdate={!!selectedOrder?.id}
            />
          </form>
        </Form>
      </V2Loader>
    </div>
  );
}
